import {
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Empty,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import axios from 'axios';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { read as xlsxRead, utils as xlsxUtils } from 'xlsx';

import ProductImage from '../../../components/productImage';
import { isUserFacingMessage } from '../../../utils/apiError';
import {
  SPREADSHEET_ACCEPT,
  findEmptySheetError,
  findSpreadsheetFileError,
  prepareProductImportRows,
} from '../../../utils/excelImport';
import { formatCurrency } from '../../../utils/format.js';
import { AddOrderDeliveryContext } from './AddOrderDeliveryContext';
import ModalSelectProduct from './ModalSelectProduct.js';
import ProductUnitField from './ProductUnitField';
import './warehouse_branch_style.less';

const { confirm } = Modal;
const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 280;

/** Агуулахын select-ийн өргөн — картын өргөнөөр сунахгүй, баруун талд зай үлдэнэ */
const SELECT_WIDTH = 260;

/** Excel-ээр бөөнөөр бараа хайх загварын файл */
const TEMPLATE_URL =
  'https://ppstorage-prod.s3.ap-southeast-1.amazonaws.com/mass_import_templates/Mass_product_find_templateASV.xlsx';

/** Excel-ийн алдааны мэдэгдэлд харуулах мөрийн тоо — бүхнийг харуулбал хэт урт */
const MAX_SHOWN_IMPORT_ERRORS = 8;

/** Тоог тооцоололд оруулахад NaN гарахаас хамгаална */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Warehouse = ({ id, products, form, setWarehouse, selectedCustomer }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [wareHouseCode, setWareHouseCode] = useState('');
  const errorList = useRef([]);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  const addContext = useContext(AddOrderDeliveryContext);

  const warehouses = useMemo(() => {
    /**
     * ЗӨВХӨН бусад блок дээр сонгогдсон агуулахуудыг сонголтоос хасна.
     * Өмнө нь формын одоогийн утгатай тэнцүү эсэхээр шүүдэг байсан — refresh
     * хийхэд формын утга нь сэргээгүй байхад `selectedWareHouses` нь sessionStorage-
     * оос сэргэдэг тул өөрийн сонгосон агуулах нь "бусдын сонгосон" гэж хасагдаж,
     * жагсаалтаас алга болдог байсан. `id`-аар шүүх нь энэ зөрүүнээс хамаарахгүй.
     */
    const takenByOtherBlocks = (addContext.selectedWareHouses ?? [])
      .filter((item) => item.id !== id)
      .map((item) => item.value);

    const reference = addContext?.reference?.warehouse;
    return {
      types: reference?.type?.filter((type) => !takenByOtherBlocks.includes(type)),
      detail: reference?.detail,
    };
  }, [addContext.selectedWareHouses, addContext?.reference?.warehouse, id]);

  /**
   * Draft-аас сэргэсэн утгыг формоос локал state рүү авна.
   *
   * Хэрэв тэр агуулах одоогийн гэрээнд БАЙХГҮЙ бол (жиш нь merchant солигдсон,
   * эсвэл гэрээнээс агуулах хасагдсан) Select дээр хадаад үлдэхгүй, цэвэрлэнэ —
   * эс бөгөөс барааны хайлт байхгүй агуулахаар явна. `types` нь хоосон үед
   * (reference хараахан татагдаагүй) цэвэрлэхгүй.
   */
  useEffect(() => {
    const fieldName = `wh-code-${id}`;
    const current = form.getFieldValue(fieldName);
    const availableTypes = warehouses?.types;

    if (current && availableTypes?.length && !availableTypes.includes(current)) {
      form.setFieldsValue({ [fieldName]: undefined });
      setWareHouseCode('');
      // setFieldsValue нь onValuesChange-ыг дуудахгүй тул draft-ыг өөрсдөө шинэчилнэ
      addContext.setFormDataStep1?.((prev) => ({ ...prev, formValues: form.getFieldsValue() }));
      return;
    }

    setWareHouseCode(current);
    //eslint-disable-next-line
  }, [warehouses?.types]);

  const closeModal = () => setIsModalVisible(false);

  const onChangeWareHouse = (val) => {
    setWareHouseCode(val);
    setWarehouse(val);
    addContext.addSelectedWareHouse({ id, value: val });
    addContext.clearWareHouseProducts(id);
  };

  const onClickSelectProduct = () => {
    if (!wareHouseCode) {
      message.error('Бараа татах агуулахаа сонгоно уу!');
      return;
    }
    setIsModalVisible(true);
  };

  const getProductsData = async (searchData) => {
    setImporting(true);
    const multiProductFetch = await axios
      .post(
        '/api/v1.0/product/rawListBySkuBarcode',
        { searchData, wareHouseCode },
        // Алдааны мессежийг доор өөрсдөө тодорхойлж харуулна
        { skipErrorToast: true }
      )
      .then((response) => response.data)
      .catch((err) => {
        console.error('rawListBySkuBarcode error: ', err);

        /**
         * Сервер тал олдохгүй SKU дээр HTTP 500 буцааж, дотоод JS алдааны
         * текстээ мессеж болгодог (`Cannot set properties of undefined`).
         * Хэрэглэгчид ямар мөр буруу байсныг хэлж чадахгүй тул юуг шалгахыг
         * зааж өгнө. Серверийн монгол шалтгаан ирсэн бол түүнийг харуулна.
         */
        message.error(
          isUserFacingMessage(err?.response?.data?.message_mn)
            ? err.response.data.message_mn
            : 'Файлд байгаа SKU / бар код бүртгэлээс олдсонгүй. Загварын мөрүүдийг шалгаад дахин оруулна уу.',
          // Урт тайлбар тул уншиж амжихаар удаан харуулна
          10
        );
        return null;
      })
      .finally(() => setImporting(false));

    if (!multiProductFetch?.result) return;

    if (multiProductFetch.error) {
      errorList.current = multiProductFetch.result;
      setErrorModalOpen(true);
      return;
    }

    const formValues = {};
    multiProductFetch.result.forEach((item, index) =>
      Object.assign(formValues, { [`warehous${index}`]: { totalUnit: item.totalUnit } })
    );
    // Бусад блокийн (салбар/өөр агуулах) тоо ширхэг арчигдахгүй байхаар нэгтгэнэ
    form.setFieldsValue({
      products: { ...(form.getFieldValue('products') ?? {}), ...formValues },
    });

    multiProductFetch.result.forEach((item) => {
      item.productLocationCode = wareHouseCode;
      item.productLocation = 'W';
    });
    addContext.updateWarehouseProducts(id, multiProductFetch.result);
  };

  const onUploadExcel = async (eventFile) => {
    if (!eventFile) return true;
    if (!wareHouseCode) {
      message.error('Бараа татах агуулахаа сонгоно уу!');
      return false;
    }

    // Excel биш файл чимээгүй уншигдаж байсныг зогсооно
    const fileError = findSpreadsheetFileError(eventFile);
    if (fileError) {
      message.error(fileError);
      return false;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      let rows;
      try {
        const workbook = xlsxRead(e.target.result, { type: 'array', raw: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        rows = xlsxUtils.sheet_to_json(worksheet, { raw: false });
      } catch (error) {
        // .xlsx нэртэй эвдэрсэн файл сервер дээр HTTP 500 үүсгэдэг байсныг зогсооно
        console.error('Excel read error: ', error);
        message.error('Файлыг уншиж чадсангүй. Excel загварыг татаж дахин бөглөнө үү.', 5);
        return;
      }

      // Мөр байхгүй файл чимээгүй өнгөрдөг байсныг зогсооно
      const emptyError = findEmptySheetError(rows);
      if (emptyError) {
        message.warning(emptyError);
        return;
      }

      /**
       * Мөрүүдийг ИЛГЭЭХЭЭС ӨМНӨ шалгана. Сервер тал бутархай тоо хэмжээг
       * чимээгүй тайрч, олдохгүй SKU дээр дотоод JS алдаа буцаадаг тул аль мөр
       * буруу байгааг эндээс монгол хэлээр хэлнэ.
       */
      const { rows: preparedRows, errors } = prepareProductImportRows(rows);

      if (errors.length) {
        message.error(
          <div style={{ textAlign: 'left' }}>
            <strong>Excel файлд алдаа байна</strong>
            {errors.slice(0, MAX_SHOWN_IMPORT_ERRORS).map((error) => (
              <div key={error}>{error}</div>
            ))}
            {errors.length > MAX_SHOWN_IMPORT_ERRORS && <div>… нийт {errors.length} алдаа</div>}
          </div>,
          // Мөр бүрийн алдааг уншиж амжихаар удаан харуулна
          10
        );
        return;
      }

      await getProductsData(preparedRows);
    };
    reader.readAsArrayBuffer(eventFile);
    return false;
  };

  const removeProduct = (record) =>
    confirm({
      title: 'Барааг устгах уу?',
      okText: 'Устгах',
      cancelText: 'Цуцлах',
      okButtonProps: { danger: true },
      onOk: () => {
        form.setFieldsValue(form.getFieldsValue());
        addContext.removeProductWarehouse(id, record._id);
      },
    });

  const columns = [
    {
      key: 'index',
      title: '№',
      width: 48,
      align: 'center',
      render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      key: 'product',
      title: 'Бараа',
      render: (_, record) => (
        <Space size={12} align="center">
          <ProductImage
            src={record?.imageCover}
            alt={record?.title ?? 'Барааны зураг'}
            size={IMAGE_SIZE}
            style={{
              objectFit: 'contain',
              borderRadius: 4,
              border: '1px solid #eaecf0',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
            }}
          />
          <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
            <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: record?.title }}>
              {record?.title ?? '-'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {record?.productSku}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      key: 'price',
      title: 'Нэгжийн үнэ',
      align: 'right',
      width: 140,
      render: (_, record) => formatCurrency(num(record?.price)),
    },
    {
      key: 'availableStock',
      title: 'Үлдэгдэл',
      align: 'right',
      width: 110,
      render: (_, record) => (record?.availableStock != null ? `${record.availableStock} ш` : '-'),
    },
    {
      key: 'totalUnit',
      title: 'Тоо ширхэг',
      align: 'right',
      width: 160,
      render: (_, record, index) => (
        <ProductUnitField
          form={form}
          name={['products', `warehous${index}`, 'totalUnit']}
          availableStock={record?.availableStock}
          defaultValue={record?.totalUnit}
          onChangeUnit={(val) => addContext.onChangeProductUnitWareHouse(id, index, val)}
        />
      ),
    },
    {
      key: 'total',
      title: 'Нийт үнэ',
      align: 'right',
      width: 150,
      render: (_, record) => (
        <Text strong>{formatCurrency(num(record?.price) * num(record?.totalUnit))}</Text>
      ),
    },
    {
      key: 'action',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Устгах">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeProduct(record)}
          />
        </Tooltip>
      ),
    },
  ];

  /** Хүснэгтийн нийлбэр мөр — тоо ширхэг, нийт үнэ */
  const renderSummary = (rows = []) => {
    const totalUnit = rows.reduce((sum, row) => sum + num(row?.totalUnit), 0);
    const totalPrice = rows.reduce((sum, row) => sum + num(row?.totalUnit) * num(row?.price), 0);

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={4} align="right">
          <Text type="secondary">Нийт</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          <Text strong>{`${totalUnit} ш`}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">
          <Text strong>{formatCurrency(totalPrice)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} />
      </Table.Summary.Row>
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[16, 8]} align="bottom">
        {/* Select нь дэлгэцийн хэмжээнээс үл хамааран богино — үлдсэн зай нь чөлөөтэй */}
        <Col flex={`0 0 ${SELECT_WIDTH}px`} style={{ maxWidth: '100%' }}>
          <Form.Item
            label="Бараа татах агуулах"
            name={`wh-code-${id}`}
            rules={[{ required: true, message: 'Бараа татах агуулахаа сонгоно уу!' }]}
            style={{ marginBottom: 0 }}
          >
            <Select placeholder="Агуулах сонгох" onChange={onChangeWareHouse}>
              {warehouses?.types?.map((type) => (
                <Select.Option value={type} key={type}>
                  {warehouses?.detail?.[type]?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col flex="auto">
          <Space size={8} wrap>
            <Button icon={<DownloadOutlined />} href={TEMPLATE_URL}>
              Excel загвар
            </Button>
            <Upload
              beforeUpload={onUploadExcel}
              showUploadList={false}
              accept={SPREADSHEET_ACCEPT}
              disabled={importing}
            >
              <Button icon={<FileExcelOutlined />} loading={importing}>
                Excel-ээр нэмэх
              </Button>
            </Upload>
            <Button type="primary" icon={<PlusOutlined />} onClick={onClickSelectProduct}>
              Бараа нэмэх
            </Button>
          </Space>
        </Col>
      </Row>

      {products?.length ? (
        <Table
          columns={columns}
          dataSource={products}
          rowKey={(record, index) => record?._id ?? index}
          size="middle"
          pagination={false}
          summary={renderSummary}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary" style={{ fontSize: 13 }}>
              {wareHouseCode
                ? '"Бараа нэмэх" товчоор бараагаа сонгоно уу.'
                : 'Эхлээд бараа татах агуулахаа сонгоно уу.'}
            </Text>
          }
          style={{ margin: 0, padding: '24px 0' }}
        />
      )}

      <Modal
        title="Excel-р оруулсан файл дээр алдаа гарлаа"
        open={isErrorModalOpen}
        onOk={() => setErrorModalOpen(false)}
        onCancel={() => setErrorModalOpen(false)}
        footer={null}
      >
        <Table
          dataSource={errorList.current}
          rowKey={(record, index) => record?.key ?? index}
          size="small"
          pagination={false}
          scroll={{ y: 320 }}
          columns={[
            { title: 'Барааны нэр', dataIndex: 'key', key: 'key' },
            { title: 'Дутсан тоо', dataIndex: 'minimum', key: 'minimum', align: 'right' },
          ]}
        />
      </Modal>

      {isModalVisible && (
        <ModalSelectProduct
          visible={isModalVisible}
          onCancel={closeModal}
          selectedProducts={products}
          wareHouseCode={wareHouseCode}
          id={id}
          selectedCustomer={selectedCustomer}
        />
      )}
    </Space>
  );
};

export default Warehouse;
